import { create } from 'zustand';
import {
  ensureDatabaseUpdated,
  getDatabaseUpdateStatus,
  FAILED_STATES,
  type DatabaseUpdateRun,
} from '../api/databaseUpdateApi';

/**
 * The login check for the selected connection (see DatabaseUpdateGate).
 *
 *   checking  the one fast Ensure call is in flight
 *   updating  the database is being updated; poll Status every 3 s
 *   failed    the update didn't finish (or we lost track of it): Continue / Try again
 *   ready     let the user in
 *
 * It fails open: if the check itself can't be made (network, an API build
 * without the endpoint), the user goes in. Blocking a facility because our
 * bookkeeping is down would be worse than the problem it guards against.
 */
export type GatePhase = 'checking' | 'updating' | 'failed' | 'ready';

export interface GateSnapshot {
  phase: GatePhase;
  run: DatabaseUpdateRun | null;
  /** Someone else's login (or a tablet, or the print agent) started this update. */
  startedByOther: boolean;
  /** Past the "longer than usual" mark (20 minutes). */
  slow: boolean;
  /** Failed because Status stopped answering, not because the update failed. */
  lostContact: boolean;
  /** Client clock when `run` was read, so elapsed time can tick between polls. */
  receivedAt: number;
}

export interface WatchDeps {
  ensure: () => Promise<DatabaseUpdateRun>;
  status: (runId?: number) => Promise<DatabaseUpdateRun>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
  pollMs?: number;
  /** Consecutive failed polls tolerated (a dropped poll is not a failed update). */
  maxPollErrors?: number;
  slowAfterMs?: number;
}

export const POLL_MS = 3000;
export const MAX_POLL_ERRORS = 5;
export const SLOW_AFTER_MS = 20 * 60 * 1000;

/**
 * Runs the check to its end. `emit` receives every change; `isCancelled`
 * stops it quietly (the user switched connection or chose Continue).
 */
export async function watchDatabaseUpdate(
  deps: WatchDeps,
  emit: (patch: Partial<GateSnapshot>) => void,
  isCancelled: () => boolean,
): Promise<'ready' | 'failed' | 'cancelled'> {
  const { pollMs = POLL_MS, maxPollErrors = MAX_POLL_ERRORS, slowAfterMs = SLOW_AFTER_MS } = deps;

  let run: DatabaseUpdateRun;
  try {
    run = await deps.ensure();
  } catch {
    if (isCancelled()) return 'cancelled';
    emit({ phase: 'ready' });
    return 'ready';
  }
  if (isCancelled()) return 'cancelled';
  if (run.state !== 'Running') {
    emit({ phase: 'ready', run });
    return 'ready';
  }

  const waitStart = deps.now();
  let runId = run.runId;
  emit({ phase: 'updating', run, startedByOther: run.startedHere === false, receivedAt: deps.now(), slow: false, lostContact: false });

  let errors = 0;
  for (;;) {
    await deps.sleep(pollMs);
    if (isCancelled()) return 'cancelled';

    let next: DatabaseUpdateRun;
    try {
      next = await deps.status(runId);
      // The server that ran it stopped: a new check picks up where it left off.
      if (next.state === 'Abandoned') next = await deps.ensure();
      errors = 0;
    } catch {
      if (isCancelled()) return 'cancelled';
      if (++errors >= maxPollErrors) {
        emit({ phase: 'failed', lostContact: true });
        return 'failed';
      }
      continue;
    }
    if (isCancelled()) return 'cancelled';

    if (next.runId) runId = next.runId;
    if (next.state === 'Running') {
      emit({ run: next, receivedAt: deps.now(), slow: deps.now() - waitStart > slowAfterMs });
      continue;
    }
    if (FAILED_STATES.has(next.state)) {
      emit({ phase: 'failed', run: next, lostContact: false });
      return 'failed';
    }
    emit({ phase: 'ready', run: next });
    return 'ready';
  }
}

const defaultDeps: WatchDeps = {
  ensure: ensureDatabaseUpdated,
  status: getDatabaseUpdateStatus,
  sleep: (ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

let deps: WatchDeps = defaultDeps;

/** Test hook. */
export function setDbUpdateDeps(overrides: Partial<WatchDeps> | null) {
  deps = overrides ? { ...defaultDeps, ...overrides } : defaultDeps;
}

interface DbUpdateState extends GateSnapshot {
  /** The connection this state is about; null until the first check. */
  connectionId: number | null;
  generation: number;
  /** Starts the check for a connection, once per connection per tab. */
  check: (connectionId: number) => void;
  /**
   * An API call was answered 503 "database-updating" (another user's login, a tablet or the print
   * agent started an update after this page got in): show the updating screen again and follow
   * the run until it ends. A no-op while this connection is already being checked or watched.
   */
  markUpdating: (connectionId: number) => void;
  /** "Try again": check the same connection again now. */
  retry: () => void;
  /** "Continue": go in without waiting any longer. */
  continueAnyway: () => void;
}

const initial: GateSnapshot = {
  phase: 'checking',
  run: null,
  startedByOther: false,
  slow: false,
  lostContact: false,
  receivedAt: 0,
};

export const useDbUpdateStore = create<DbUpdateState>((set, get) => {
  const start = (connectionId: number, phase: GatePhase = 'checking') => {
    const generation = get().generation + 1;
    set({ ...initial, phase, connectionId, generation });
    void watchDatabaseUpdate(
      deps,
      (patch) => {
        if (get().generation === generation) set(patch);
      },
      () => get().generation !== generation,
    );
  };

  return {
    ...initial,
    connectionId: null,
    generation: 0,
    check: (connectionId) => {
      if (connectionId <= 0 || get().connectionId === connectionId) return;
      start(connectionId);
    },
    markUpdating: (connectionId) => {
      if (connectionId <= 0) return;
      const s = get();
      if (s.connectionId === connectionId && (s.phase === 'checking' || s.phase === 'updating')) return;
      // Straight to the updating screen (no loading flash): the API has just said it is updating.
      start(connectionId, 'updating');
    },
    retry: () => {
      const id = get().connectionId;
      if (id) start(id);
    },
    continueAnyway: () => set((s) => ({ phase: 'ready', generation: s.generation + 1 })),
  };
});

/**
 * For a request turned away with 503 "database-updating": resolves true once the update the page
 * is watching for `connectionId` has ended (finished, failed, or the user chose Continue), so the
 * request can be sent again; false if the user moved to another connection meanwhile.
 */
export function waitForDatabaseUpdate(connectionId: number): Promise<boolean> {
  const settled = (s: DbUpdateState): boolean | null => {
    if (s.connectionId !== connectionId) return false;
    return s.phase === 'ready' || s.phase === 'failed' ? true : null;
  };
  return new Promise((resolve) => {
    const now = settled(useDbUpdateStore.getState());
    if (now !== null) {
      resolve(now);
      return;
    }
    const unsubscribe = useDbUpdateStore.subscribe((s) => {
      const result = settled(s);
      if (result === null) return;
      unsubscribe();
      resolve(result);
    });
  });
}
